import React, { useState, useEffect } from "react";
import { Box, Grid, Paper } from "@mui/material";
import jss from '../../pages/flight/FlightCommonJss'
import FlightControlJobs from '../../pages/flight/FlightControlJobs';
import FlightControlLogs from '../../pages/flight/FlightControlLogs';
import JobSettings from '../../pages/flight/JobSettings';
import FlightService from "../../services/FlightService";
import JobLog from "./JobLog";
import FlightClients from "./FlightClients";
import TipContext from "../../providers/GlobalTip";
import moment from "moment";

type FlightClient = {
	Id?: number | string | null;
	id?: number | string | null;
	PointOfSaleId?: number | string | null;
	point_of_sale_id?: number | string | null;
	pointOfSaleId?: number | string | null;
};

type FlightJobSettings = {
	id?: string | number | null;
	client_id?: string | number | null;
	enabled?: boolean | number;
	[key: string]: unknown;
};

type FlightLog = {
	id: string | number;
	client?: FlightClient | null;
	[key: string]: unknown;
};

type RunOptions = {
	period?: string | null;
	sinceLastRun?: boolean;
	loadFullInventory?: boolean;
	skipInventory?: boolean;
	onOrderOnly?: boolean;
	grabAllOnOrder?: boolean;
};

const cssInJs = {
	gridItem: {
		maxHeight: '1000px',
		overflowY: 'auto',
	},
}

function FlightControl() {
	const tip_context = TipContext.useMessage()

	const getClientId = (client: FlightClient | null) => client?.Id ?? client?.id ?? null;
	const getClientPosId = (client: FlightClient | null) => client?.PointOfSaleId ?? client?.point_of_sale_id ?? client?.pointOfSaleId ?? null;
	const hasFlightJobId = (job: FlightJobSettings | null | undefined): job is FlightJobSettings => {
		return job != null && job.id != null && String(job.id).trim() !== '';
	};
	const normalizeTimeOfDay = (value: unknown) => {
		if (value == null || value === '' || value === 0 || value === '0') {
			return null;
		}
		if (typeof value === 'string') {
			const match = value.trim().match(/^(\d{2}:\d{2}:\d{2})/);
			if (match) {
				return match[1];
			}
		}
		return value;
	};

	// Clients
	const [clients, setClients] = useState<FlightClient[]>([]);
	const [selectedClient, setSelectedClient] = React.useState<FlightClient | null>(null);
	// Ref mirrors selectedClient synchronously so in-flight async fetches (e.g. the
	// initial "recent logs" load) can detect if a client was selected/changed since
	// the request was made and avoid clobbering more relevant results.
	const selectedClientRef = React.useRef<FlightClient | null>(null);

	// Job Settings
	const [flightJobSettings, setFlightJobSettings] = useState<FlightJobSettings[]>([]);
	const [selectedFlightJob, setSelectedFlightJob] = React.useState<FlightJobSettings | null>(null);
	const [jobSettingsLoading, setJobSettingsLoading] = React.useState(false);

	// Flight Logs
	const [allRecentFlightLogs, setAllRecentFlightLogs] = useState<FlightLog[]>([]);
	const [searchedFlightLogs, setSearchedFlightLogs] = useState<FlightLog[]>([]);
	const [selectedFlightJobLog, setSelectedFlightJobLog] = React.useState<FlightLog | null>(null);
	const [jobLogLoading, setJobLoading] = useState(false);

	useEffect(() => {
		document.title = 'Retail ORBIT: Flight Control'
	}, [])

	// keep the ref in sync with the latest selectedClient on every render
	selectedClientRef.current = selectedClient;

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const _clientsResponse = await (FlightService as any).getFlightClients() as FlightClient[] | undefined;
				const _recentFlightLogsResponse = await (FlightService as any).getRecentFlightJobLogs() as FlightLog[] | undefined;

				// Request.get() swallows non-401 HTTP errors (e.g. 403 Unauthorized) and
				// resolves with undefined instead of throwing, so detect that case here
				// and surface it to the user instead of silently showing an empty page.
				if (_clientsResponse === undefined || _recentFlightLogsResponse === undefined) {
					tip_context.show('You do not have access to view Flight Control data');
				}

				const _clients = _clientsResponse ?? [];
				setClients(_clients);

				const _recentFlightLogs = _recentFlightLogsResponse ?? [];
				setAllRecentFlightLogs(_recentFlightLogs);

				// Only replace the visible log list if no client has been selected in the
				// meantime; otherwise this stale response would clobber the client-specific
				// logs that were already loaded.
				if (!selectedClientRef.current) {
					setSearchedFlightLogs(_recentFlightLogs);
				}
			} catch (error) {
				console.error('Error', error);
			}
		};
		fetchData();
	}, []);

	React.useEffect(() => {
		setSelectedFlightJob(null);
		if (selectedClient) {
			const selectedClientId = getClientId(selectedClient);
			if (!selectedClientId) {
				setJobSettingsLoading(false);
				setSearchedFlightLogs(allRecentFlightLogs);
				return;
			}

			setJobSettingsLoading(true);

			const fetchFlightLogs = async () => {
				try {
					const _flightLogs = (await (FlightService as any).getFlightJobLogsForClient(selectedClientId) as FlightLog[] | undefined) ?? [];
					// Ignore this response if the user has since switched to a different
					// client (or deselected) while the request was in flight.
					if (getClientId(selectedClientRef.current) === selectedClientId) {
						setSearchedFlightLogs(_flightLogs);
					}
				} catch (error) {
					console.error('Error', error);
				}
			};
			fetchFlightLogs();

			const clientSettings = flightJobSettings.find((setting) => String(setting.client_id) === String(selectedClientId) && hasFlightJobId(setting));
			if (!clientSettings) {
				const fetchData = async () => {
					try {
						const _flightJobSettingsResponse = await (FlightService as any).getFlightJobSettingsForClient(selectedClientId) as FlightJobSettings | null;
						const _flightJobSettings = hasFlightJobId(_flightJobSettingsResponse) ? _flightJobSettingsResponse : null;

						if (_flightJobSettings != null) {
							setFlightJobSettings((prev) => {
								const withoutClient = prev.filter((setting) => String(setting.client_id) !== String(selectedClientId));
								return [...withoutClient, _flightJobSettings];
							});
						}

						setSelectedFlightJob(_flightJobSettings);
					} catch (error) {
						console.error('Error', error);
					}

					setJobSettingsLoading(false);
				};
				fetchData();
			} else {
				setSelectedFlightJob(clientSettings);
				setJobSettingsLoading(false);
			}
		} else {
			setSearchedFlightLogs(allRecentFlightLogs);
			setJobSettingsLoading(false);
		}
	}, [selectedClient]);

	const handleJobClick = (selectedFlightJob: FlightJobSettings | null) => {
		setSelectedFlightJob(selectedFlightJob);
	};

	const handleRefreshJobLog = async (jobLog: FlightLog | null) => {
		if (!jobLog || !jobLog.id) {
			return;
		}

		(FlightService as any).getFlightJobLogById({ flightJobLogId: jobLog.id, relationsToLoad: ['client']}).then((response: FlightLog) => {
			const _jobLog = response;
			setSelectedFlightJobLog(_jobLog);

			// replace in logs list
			const _logs = searchedFlightLogs.map((log: FlightLog) => {
				if (log.id === jobLog.id) {
					return _jobLog;
				}
				return log;
			});
			setSearchedFlightLogs(_logs);

			tip_context.showSuccess('Job log refreshed');
		}).catch((error: unknown) => {
			console.error('Error refreshing job log', error);
			tip_context.show('Error refreshing job log');
		});
	}

	const handleJobLogClick = (selectedFlightJobLog: FlightLog) => {
		setSelectedFlightJobLog(selectedFlightJobLog);
	}

	// const handleAddJob = () => {
	// 	setShowJobDetails((prev) => !prev);
	// };

	const handleSaveFlightJob = async (flightJob: FlightJobSettings) => {
		try {
			const payload: FlightJobSettings = {
				...flightJob,
				time_of_day: normalizeTimeOfDay(flightJob.time_of_day),
			};
			const response = await (FlightService as any).updateFlightJob(payload.id, payload) as FlightJobSettings | null | undefined;
			const savedFlightJob: FlightJobSettings = hasFlightJobId(response) ? response : payload;
			
			tip_context.showSuccess('Job settings saved');

			// update flight job settings
			const updatedSettings = flightJobSettings.map((setting: FlightJobSettings) => {
				if (String(setting.id) === String(savedFlightJob.id)) {
					return savedFlightJob;
				}
				return setting;
			});

			setFlightJobSettings(updatedSettings);

			// update selected flight job
			setSelectedFlightJob(savedFlightJob);
		} catch (error) {
			console.log(error)
			tip_context.show('Error saving flight job');
		}
	}

	const handleToggleJob = async (enabled: boolean) => {
		try {
			const selectedClientId = getClientId(selectedClient);
			const hasExistingJob = hasFlightJobId(selectedFlightJob);
			if (selectedClient && !hasExistingJob) {
				await (FlightService as any).createFlightJob({
					client_id: selectedClientId,
					enabled: enabled,
					job_type_id: 2,
					time_of_day: 0,
					interval: 202402
				}).then((response: FlightJobSettings) => {
					console.log('Created job', response);
					setSelectedFlightJob(response);
					setFlightJobSettings((prev) => {
						const withoutClient = prev.filter((setting) => String(setting.client_id) !== String(selectedClientId));
						return [...withoutClient, response];
					});
				});
			} else {
				if (!hasExistingJob) {
					return;
				}

				await (FlightService as any).updateFlightJob(selectedFlightJob.id, {
					...selectedFlightJob,
					enabled: enabled,
				}).then((response: FlightJobSettings) => {
					setSelectedFlightJob(response);
					setFlightJobSettings((prev) => prev.map((setting) => {
						if (String(setting.id) === String(response.id)) {
							return response;
						}
						return setting;
					}));
				});
			}

			tip_context.showSuccess('Job toggled');
		} catch (error) {
			console.log('Error toggling job', error);
			tip_context.show('Error toggling job');
		}
	}

	const handleRun = async ({ period = null, sinceLastRun = false, loadFullInventory = false, skipInventory = false, onOrderOnly = false, grabAllOnOrder = false }: RunOptions) => {
		if (!selectedClient || !selectedFlightJob || !selectedFlightJob.id) {
			return;
		}

		const _currentClientId = getClientId(selectedClient);

		try {
			// if not period, default to today formatted as yyyymm
			const _period = period ? moment(period).format('YYYYMM') : moment().format('YYYYMM');

			(FlightService as any).runFlightJob(selectedFlightJob.id, {
				period: _period,
				sinceLastRun: sinceLastRun,
				loadFullInventory: loadFullInventory,
				skipInventory: skipInventory,
				onOrderOnly: onOrderOnly,
				grabAllOnOrder: grabAllOnOrder
			}).then((response: FlightLog) => {
				// if selectedClient id is still the same, append to logs
				// if (selectedClient.Id === _currentClientId) {
				// 	setSearchedFlightLogs([response, ...searchedFlightLogs]);
				// 	tip_context.showSuccess('Job completed and appended to log list');
				// }
			}).catch((error: unknown) => {
				console.error('Error running job', error);
				tip_context.show('Error running job');
			});

			tip_context.showSuccess('Job started');

			// get latest logs
			handleReloadLogs(true);
		} catch (error) {
			console.error('Error running job', error);
			tip_context.show('Error running job');
		}
	}

	const handleReloadLogs = async (selectLatest = false) => {
		try {
			const _recentFlightLogs = (await (FlightService as any).getRecentFlightJobLogs() as FlightLog[] | undefined) ?? [];
			setAllRecentFlightLogs(_recentFlightLogs);
			
			if (selectedClient) {
				const selectedClientId = getClientId(selectedClient);
				if (!selectedClientId) {
					setSearchedFlightLogs(_recentFlightLogs);
					if (selectLatest) {
						setSelectedFlightJobLog(_recentFlightLogs[0]);
					}
					return;
				}

				const _flightLogs = (await (FlightService as any).getFlightJobLogsForClient(selectedClientId) as FlightLog[] | undefined) ?? [];
				setSearchedFlightLogs(_flightLogs);
				if (selectLatest) {
					setSelectedFlightJobLog(_flightLogs[0]);
				}
			} else {
				setSearchedFlightLogs(_recentFlightLogs);
				if (selectLatest) {
					setSelectedFlightJobLog(_recentFlightLogs[0]);
				}
			}
		} catch (error) {
			console.error('Error', error);
		}
	}

	return (
		<Box sx={{ py: 2, minWidth: 1100 }}>
			<Grid container spacing={2}>
				{/* Title */}
				<Grid item xs={12}>
					<Box sx={jss.title}>Flight Control Page</Box>
					{/* <Paper elevation={2} sx={{ mb: 2 }}>
						<Box>
							This page is a still a work in progress, so it may be missing some features. Known missing features include: searching logs by date (more than 7 days old) when a client isn't selected. <strong>NOTE that enable/disable jobs is not currently functional for non Lightspeed Clients</strong>.
						</Box>
					</Paper> */}
				</Grid>

				{/* FlightControlJobs */}
				<Grid item xs={12} sm={4} sx={cssInJs.gridItem}>
					<FlightClients
						clients={clients}
						setSelectedClient={setSelectedClient}
						selectedClient={selectedClient}
						loading={jobSettingsLoading || jobLogLoading}
					/>
				</Grid>

				{/* FlightControlLogs */}
				<Grid item xs={12} sm={4} sx={cssInJs.gridItem}>
					<FlightControlLogs
						onJobLogClick={handleJobLogClick}
						flightLogs={searchedFlightLogs}
						selectedClient={selectedClient}
						selectedJobLog={selectedFlightJobLog}
						handleReloadData={handleReloadLogs}
						tipContext={tip_context}
					/>
				</Grid>

				{/* FlightControlLogs and JobSettings */}
				<Grid item xs={12} sm={4}>
					{/* JobSettings */}
					{(selectedClient && !jobSettingsLoading) ?
						<JobSettings
							jobSettings={selectedFlightJob}
							onSave={handleSaveFlightJob}
							toggleJob={handleToggleJob}
							onRun={handleRun}
							tipContext={tip_context}
							client={selectedClient}
							posId={getClientPosId(selectedClient)}
							reloadLogs={handleReloadLogs}
						/>
					: null}

					{/* JobLog */}
					{selectedFlightJobLog && selectedFlightJobLog !== null && !jobLogLoading && (
						<JobLog
							selectedFlightJobLog={selectedFlightJobLog}
							onRefresh={handleRefreshJobLog}
						/>
					)}
				</Grid>
			</Grid>
		</Box>
	)
}

export default FlightControl